import type { DataGridColumn, PreviewImage } from "@/components/kit";

import { Button, Tooltip } from "@heroui/react";
import { Sheet } from "@heroui-pro/react";
import { FolderTree, Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { AddSubCategoryModal } from "../sub-categories/add-sub-category";
import { EditSubCategoryModal } from "../sub-categories/edit-sub-category";
import { ManageParentCategoriesModal } from "../sub-categories/manage-parent-categories";

import { DataTable, ImagePreviewModal, ImageTile } from "@/components/kit";
import { Category, SubCategory } from "@/types";

interface ViewCategoryModalProps {
  category: Category | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

function Fact({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-muted text-xs">{label}</span>
      <span className="truncate text-sm font-medium">{children}</span>
    </div>
  );
}

export function ViewCategoryModal({
  category,
  isOpen,
  onClose,
  onRefresh,
}: ViewCategoryModalProps) {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editing, setEditing] = useState<SubCategory | null>(null);
  const [preview, setPreview] = useState<PreviewImage | null>(null);
  const [manageParentsSub, setManageParentsSub] =
    useState<SubCategory | null>(null);

  const columns = useMemo<DataGridColumn<SubCategory>[]>(
    () => [
      {
        id: "name",
        header: "Subcategory",
        isRowHeader: true,
        cell: (sub) => (
          <div className="flex items-center gap-3">
            {/* A mouse shortcut to the full image: not a tab stop, so the row's name stays the subcategory's. */}
            <div
              className="cursor-pointer"
              onClick={() => setPreview({ url: sub.image, name: sub.name })}
            >
              <ImageTile size="sm" src={sub.image} />
            </div>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-sm font-medium">{sub.name}</span>
              <span className="text-muted self-start truncate text-xs" dir="rtl">
                {sub.nameAr}
              </span>
            </div>
          </div>
        ),
      },
      {
        id: "id",
        header: "ID",
        width: 150,
        cell: (sub) => (
          <span className="text-muted block truncate font-mono text-xs">
            {sub.id}
          </span>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 92,
        align: "end",
        cell: (sub) => (
          <div className="flex justify-end gap-0.5">
            <Tooltip delay={300}>
              <Button
                isIconOnly
                aria-label="Edit subcategory"
                size="sm"
                variant="ghost"
                onPress={() => setEditing(sub)}
              >
                <Pencil className="size-4" />
              </Button>
              <Tooltip.Content>Edit subcategory</Tooltip.Content>
            </Tooltip>
            <Tooltip delay={300}>
              <Button
                isIconOnly
                aria-label="Manage parent categories"
                size="sm"
                variant="ghost"
                onPress={() => setManageParentsSub(sub)}
              >
                <FolderTree className="size-4" />
              </Button>
              <Tooltip.Content>Manage parent categories</Tooltip.Content>
            </Tooltip>
          </div>
        ),
      },
    ],
    [],
  );

  if (!category) return null;

  const subs = category.subCategories ?? [];

  return (
    <>
      <Sheet
        isDetached
        isOpen={isOpen}
        placement="right"
        onOpenChange={(open) => !open && onClose()}
      >
        <Sheet.Backdrop className="z-0">
          <Sheet.Content className="h-[calc(100dvh-1rem)] w-full max-w-2xl">
            <Sheet.Dialog>
              <Sheet.CloseTrigger />
              <Sheet.Header>
                <Sheet.Heading>Category Details</Sheet.Heading>
              </Sheet.Header>
              <Sheet.Body className="flex min-h-0 flex-1 flex-col gap-6">
                <div className="flex items-start gap-5">
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      setPreview({ url: category.image, name: category.name })
                    }
                  >
                    <ImageTile size="lg" src={category.image} />
                  </div>
                  <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-6 gap-y-3">
                    <Fact label="Category Name">{category.name}</Fact>
                    <Fact label="Name (Arabic)">
                      <span dir="rtl">{category.nameAr}</span>
                    </Fact>
                    <Fact label="Category ID">
                      <span className="font-mono text-xs">{category.id}</span>
                    </Fact>
                    <Fact label="Total Subcategories">{subs.length}</Fact>
                  </div>
                </div>

                <div className="flex min-h-0 flex-1 flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold">Subcategories</h3>
                    <Button size="sm" onPress={() => setIsAddOpen(true)}>
                      <Plus className="size-4" />
                      Add SubCategory
                    </Button>
                  </div>
                  <DataTable
                    columns={columns}
                    empty={{
                      title: "No subcategories yet",
                      description: "Add one to get started.",
                    }}
                    compact
                    getRowId={(sub) => sub.id}
                    label="Subcategories"
                    rows={subs}
                  />
                </div>
              </Sheet.Body>
              <Sheet.Footer className="flex justify-end">
                <Button variant="ghost" onPress={onClose}>
                  Close
                </Button>
              </Sheet.Footer>
            </Sheet.Dialog>
          </Sheet.Content>
        </Sheet.Backdrop>
      </Sheet>

      <ImagePreviewModal image={preview} onClose={() => setPreview(null)} />

      <AddSubCategoryModal
        categoryId={category.id}
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSuccess={() => {
          onRefresh();
          setIsAddOpen(false);
        }}
      />

      <EditSubCategoryModal
        categoryId={category.id}
        isOpen={!!editing}
        subCategory={subs.find((sc) => sc.id === editing?.id)}
        onClose={() => setEditing(null)}
        onSuccess={() => {
          onRefresh();
          setEditing(null);
        }}
      />

      {manageParentsSub && (
        <ManageParentCategoriesModal
          isOpen={!!manageParentsSub}
          subCategoryId={manageParentsSub.id}
          subCategoryName={manageParentsSub.name}
          onClose={() => setManageParentsSub(null)}
          onSuccess={() => {
            onRefresh();
            setManageParentsSub(null);
          }}
        />
      )}
    </>
  );
}
